import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import type { ActiveCall } from '@/redux/app/reducers/calls';
import { pressable, useDraggable } from './use-draggable';

// Mirrors the legacy #dialer (app/views/shared/_dialer.html.erb, styled in
// app/assets/stylesheets/layouts/_shared.scss) so both call flows look the same
const Panel = styled.div`
  cursor: move;
  font-family: Montserrat, sans-serif;
  background: white;
  padding: 10px;
  width: 250px;
  text-align: center;
  border-radius: 8px;
  box-shadow: 1px 1px 10px rgba(0, 0, 0, 0.5);
  pointer-events: auto;
`;

const Callee = styled.div<{ size: number }>`
  font-size: ${({ $size }) => $size}px;
  margin-bottom: 10px;
  max-width: 250px;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
  font-family: Montserrat, sans-serif;
`;

const Buttons = styled.div`
  margin: 15px 0;
`;

// &&& outranks jQuery Mobile's `html body .ui-group-theme-a a` link color
const MuteButton = styled.a<{ muted: boolean }>`
  display: inline-block;
  font-family: 'Ionicons';
  font-size: 20px;
  border: 1px solid #ddd;
  cursor: pointer;
  color: ${({ $muted }) => ($muted ? 'white' : 'black')};
  background-color: ${({ $muted }) => ($muted ? '#555' : 'transparent')};
  padding: 10px 15px;
  border-radius: 8px;
  min-width: 48px;
  text-align: center;

  &&&, &&&:hover, &&&:focus {
    color: ${({ $muted }) => ($muted ? 'white' : 'black')};
    text-decoration: none;
  }

  &:after {
    content: '${({ $muted }) => ($muted ? '\\f395' : '\\f2ec')}';
  }
`;

const HangupButton = styled.a`
  cursor: pointer;
  background-color: #d22600;
  color: white;
  padding: 10px 15px;
  border-radius: 8px;
  font-size: 20px;
  font-family: Montserrat, sans-serif;

  &&&, &&&:hover, &&&:focus {
    color: white;
    text-decoration: none;
  }
`;

// The legacy field is wrapped by jQuery Mobile (.ui-input-text), which supplies
// the box; the input itself is borderless with right-aligned digits
const DialedWrapper = styled.div`
  margin: 8px 0;
  border: 1px solid #ddd;
  border-radius: 5px;
  background: white;
  overflow: hidden;

  input {
    display: block;
    width: 100%;
    margin: 0;
    padding: 0.4em;
    border: 0;
    border-radius: 5px;
    background: transparent;
    box-shadow: none;
    outline: none;
    color: inherit;
    font-family: sans-serif;
    font-size: 16px;
    line-height: 1.4;
    text-align: right;
  }
`;

const Icon = styled.span`
  display: inline-block;
  font-family: 'Ionicons';
  margin-right: 5px;
`;

const Dialpad = styled.table`
  margin: 0 auto;
  border-spacing: 10px;

  td {
    cursor: pointer;
    padding: 10px 15px;
    border: 1px solid #777;
    border-radius: 8px;

    &:hover {
      background: rgba(0, 0, 0, 0.1);
    }
  }

  .special {
    font-family: 'Ionicons';
    font-size: 13px;
  }
`;

// Ionicons glyphs used by the legacy dialer
const HANGUP_ICON = '';
const DIALPAD_ROWS: [string, string][][] = [
  [['1', '1'], ['2', '2'], ['3', '3']],
  [['4', '4'], ['5', '5'], ['6', '6']],
  [['7', '7'], ['8', '8'], ['9', '9']],
  [['*', ''], ['0', '0'], ['#', '']],
];

const CLICK_SOUND_URL = 'https://assets.avochato.com/assets/audio/click-phone.mp3';

const zeroPad = (n: number) => n.toString().padStart(2, '0');

export const formatDuration = (seconds: number) => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor(seconds / 60) % 60;
  return `${zeroPad(hours)}:${zeroPad(minutes)}:${zeroPad(seconds % 60)}`;
};

export interface ActiveCallPanelProps {
  call: ActiveCall;
  onToggleMute: () => void;
  onSendDigits: (digits: string) => void;
  onHangup: () => void;
}

export const ActiveCallPanel = ({ call, onToggleMute, onSendDigits, onHangup }: ActiveCallPanelProps) => {
  const [duration, setDuration] = useState(0);
  const [dialed, setDialed] = useState('');
  const dialedRef = useRef<HTMLInputElement>(null);
  const clickSoundRef = useRef<HTMLAudioElement>(null);
  const draggable = useDraggable();

  useEffect(() => {
    const interval = setInterval(
      () => setDuration(Math.max(0, Math.floor((Date.now() - call.connectedAt) / 1000))),
      1000,
    );
    return () => clearInterval(interval);
  }, [call.connectedAt]);

  // Keep the latest digits in view, like the legacy dialer
  useEffect(() => {
    if (dialedRef.current) dialedRef.current.scrollLeft = 99999;
  }, [dialed]);

  const pressDigit = (digit: string) => {
    onSendDigits(digit);
    setDialed((value) => `${value}${digit}`);
    const audio = clickSoundRef.current;
    if (audio?.play) {
      audio.currentTime = 0;
      audio.playbackRate = 2;
      audio.play()?.catch(() => {});
    }
  };

  return (
    <Panel role="region" aria-label="Active call" {...draggable}>
      <Callee $size={24}>{call.contactName || 'Incoming Call'}</Callee>
      <Callee $size={22}>{call.contactPhone}</Callee>
      <div>{formatDuration(duration)}</div>
      <Buttons>
        <MuteButton aria-label={call.muted ? 'Unmute' : 'Mute'} $muted={call.muted} {...pressable(onToggleMute)} />
        {' '}
        <HangupButton {...pressable(onHangup)}>
          <Icon>{HANGUP_ICON}</Icon>
          {' Hangup'}
        </HangupButton>
      </Buttons>
      <DialedWrapper>
        <input
          ref={dialedRef}
          type="text"
          name="dialed"
          aria-label="Dialed digits"
          value={dialed}
          readOnly
        />
      </DialedWrapper>
      <Dialpad>
        <tbody>
          {DIALPAD_ROWS.map((row) => (
            <tr key={row[0][0]}>
              {row.map(([digit, label]) => (
                <td
                  key={digit}
                  data-val={digit}
                  aria-label={digit}
                  className={label === digit ? undefined : 'special'}
                  {...pressable(() => pressDigit(digit))}
                >
                  {label}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </Dialpad>
      {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
      <audio ref={clickSoundRef} src={CLICK_SOUND_URL} preload="none" />
    </Panel>
  );
};
